import { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { selectUser, setUser } from '../store/authSlice';
import API from '../api';
import CountUp from 'react-countup';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from 'recharts';
import './css/dashboard.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_META = {
  callback:     { label: 'Callback',      color: '#7c3aed' },
  interested:   { label: 'Hot Lead',      color: '#d97706' },
  intrested:    { label: 'Hot Lead',      color: '#d97706' },
  answering:    { label: 'Answering',     color: '#2563eb' },
  Notintrested: { label: 'Not Interested',color: '#dc2626' },
  sold:         { label: 'Sold',          color: '#059669' },
  approve:      { label: 'Approved',      color: '#0284c7' },
};

const PIPELINE = [
  { key: 'callback',     label: 'Callback',       color: '#7c3aed', extra: [] },
  { key: 'interested',   label: 'Hot Leads',       color: '#d97706', extra: ['intrested'] },
  { key: 'answering',    label: 'Answering',       color: '#2563eb', extra: [] },
  { key: 'Notintrested', label: 'Not Interested',  color: '#dc2626', extra: [] },
  { key: 'sold',         label: 'Sold',            color: '#059669', extra: [] },
  { key: 'approve',      label: 'Approved',        color: '#0284c7', extra: [] },
];

const TEAM_COLORS = ['#7c3aed','#d97706','#2563eb','#059669','#dc2626','#0284c7','#db2777','#0891b2'];
const AVATAR_PALETTE = ['#7c3aed','#2563eb','#059669','#d97706','#dc2626','#0284c7','#db2777'];

function avatarColor(name) {
  if (!name) return AVATAR_PALETTE[0];
  return AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length];
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// ── Custom Tooltip ────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      {label && <p className="chart-tooltip__label">{label}</p>}
      {payload.map((p, i) => (
        <div key={i} className="chart-tooltip__row">
          <span className="chart-tooltip__dot" style={{ background: p.fill || p.color }} />
          <span>{p.name || 'Leads'}:</span>
          <span className="chart-tooltip__val">{p.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color, trend, trendLabel }) {
  return (
    <div className="stat-card" style={{ '--card-accent': color }}>
      <div className="stat-card__watermark">{icon}</div>
      <div className="stat-card__top">
        <div className="stat-card__icon" style={{ background: color + '18', color }}>
          {icon}
        </div>
        {trend !== undefined && (
          <span className={`stat-card__trend ${trend > 0 ? 'up' : trend < 0 ? 'down' : 'neutral'}`}>
            {trend > 0 ? '↑' : trend < 0 ? '↓' : '—'} {trendLabel || Math.abs(trend) + '%'}
          </span>
        )}
      </div>
      <div>
        <p className="stat-card__label">{label}</p>
        <p className="stat-card__value">
          <CountUp end={value} duration={1.4} separator="," />
        </p>
        {sub && <p className="stat-card__sub">{sub}</p>}
      </div>
    </div>
  );
}

// ── Quick Action ──────────────────────────────────────────────────────────────
function QuickAction({ icon, label, to, color }) {
  return (
    <Link to={to} className="quick-action" style={{ '--qa-color': color }}>
      <span className="quick-action__icon">{icon}</span>
      <span className="quick-action__label">{label}</span>
    </Link>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const Icon = {
  leads: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  sold:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>,
  phone: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.68 2.81a2 2 0 0 1-.45 2.11L7.91 9a16 16 0 0 0 6.1 6.09l1.09-1.09a2 2 0 0 1 2.11-.45c.91.32 1.85.55 2.81.68A2 2 0 0 1 22 16.92z"/></svg>,
  fire:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>,
  doc:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  help:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  team:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  note:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  admin: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  sync:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  chart: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
export default function Index() {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const [stats,      setStats]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const role       = (user?.role || stats?.role || '').toLowerCase();
  const isAdmin    = role === 'admin';
  const isSubAdmin = role === 'sub-admin';

  // Hydrate user if missing
  useEffect(() => {
    if (!user) {
      API.get('/auth/me')
        .then(r => { if (r.data?.user) dispatch(setUser(r.data.user)); })
        .catch(() => {});
    }
  }, [user, dispatch]);

  // Fetch dashboard stats — pass uid as query param so the endpoint works
  // even when the cookie is not forwarded (reverse proxy / cross-origin live server).
  // This matches the pattern used by cbAgent, tdcbAgent, etc. in this codebase.
  useEffect(() => {
    setLoading(true);
    const uid = user?.id;
    const url = uid ? `/auth/dashboard-stats?uid=${uid}` : '/auth/dashboard-stats';
    API.get(url)
      .then(r => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [refreshKey, user?.id]);

  // ── Derived data ─────────────────────────────────────────────────────────────
  const breakdown = stats?.statusBreakdown || {};
  const totalLeads    = stats?.total ?? 0;
  const soldCount     = breakdown['sold'] || 0;
  const callbackCount = breakdown['callback'] || 0;
  const hotCount      = (breakdown['interested'] || 0) + (breakdown['intrested'] || 0);
  const convRate      = totalLeads > 0 ? Math.round((soldCount / totalLeads) * 100) : 0;
  const ansRate       = totalLeads > 0 ? Math.round(((breakdown['answering'] || 0) / totalLeads) * 100) : 0;

  // Bar chart data — status pipeline
  const barData = useMemo(() =>
    PIPELINE.map(s => ({
      name: s.label,
      value: (breakdown[s.key] || 0) + s.extra.reduce((a, k) => a + (breakdown[k] || 0), 0),
      color: s.color,
    })).filter(d => d.value > 0),
  [breakdown]);

  // Pie / donut data
  const pieData = useMemo(() =>
    barData.map(d => ({ name: d.name, value: d.value, color: d.color })),
  [barData]);

  // Team bar chart (admin)
  const teamData = useMemo(() =>
    (stats?.teamDistribution || []).map((t, i) => ({
      name: t.ass?.length > 14 ? t.ass.slice(0, 12) + '…' : (t.ass || 'N/A'),
      count: t.count,
      color: TEAM_COLORS[i % TEAM_COLORS.length],
    })),
  [stats]);

  // Radial data for conversion gauge
  const radialData = useMemo(() => [
    { name: 'Not Converted', value: 100, fill: '#f1f5f9' },
    { name: 'Converted',     value: convRate, fill: '#059669' },
  ], [convRate]);

  const recentLeads = stats?.recentLeads || [];

  const dateStr = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const roleBadgeClass =
    isAdmin    ? 'role-badge role-badge--admin' :
    isSubAdmin ? 'role-badge role-badge--subadmin' :
                 'role-badge role-badge--agent';

  return (
    <div className="db">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="db__header">
        <div>
          <h1 className="db__title">{getGreeting()}, {user?.username || 'there'}</h1>
          <p className="db__subtitle">{dateStr}</p>
        </div>
        <div className="db__header-right">
          <span className={roleBadgeClass}>{user?.role || 'User'}</span>
          {user?.ass && <span className="team-badge">{user.ass}</span>}
          <button
            className="db__refresh"
            onClick={() => setRefreshKey(k => k + 1)}
            title="Refresh data"
          >
            <span style={{ display:'flex', width:16, height:16 }}>{Icon.sync}</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="db__loading">
          <div className="db__spinner" />
          <p>Loading dashboard…</p>
        </div>
      ) : (
        <>
          {/* ── KPI Cards ──────────────────────────────────────────────────── */}
          <div className="db__kpi-grid">
            <StatCard
              icon={Icon.leads} label="Total Leads" value={totalLeads}
              sub={isAdmin ? `${stats?.totalUsers ?? 0} team members` : isSubAdmin ? 'in your team' : 'assigned to you'}
              color="#7c3aed"
            />
            <StatCard
              icon={Icon.fire} label="Hot Leads" value={hotCount}
              sub={`${totalLeads > 0 ? Math.round(hotCount / totalLeads * 100) : 0}% of pipeline`}
              color="#d97706"
              trend={hotCount > 0 ? 1 : 0}
              trendLabel={`${totalLeads > 0 ? Math.round(hotCount / totalLeads * 100) : 0}%`}
            />
            <StatCard
              icon={Icon.phone} label="Callbacks" value={callbackCount}
              sub="Pending follow-up"
              color="#2563eb"
            />
            <StatCard
              icon={Icon.sold} label="Closed / Sold" value={soldCount}
              sub={`${convRate}% conversion rate`}
              color="#059669"
              trend={convRate}
              trendLabel={`${convRate}%`}
            />
          </div>

          {/* ── Status Bar Chart + Donut ────────────────────────────────────── */}
          <div className="db__chart-grid">

            {/* Horizontal bar chart — status breakdown */}
            <div className="db__card">
              <div className="db__card-header">
                <div>
                  <h2 className="db__card-title">Lead Pipeline Breakdown</h2>
                  <p className="db__card-subtitle">Leads grouped by current status</p>
                </div>
                <Link to="/leads" className="db__card-link">View all →</Link>
              </div>
              {barData.length === 0 ? (
                <p className="db__empty">No lead data available.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={barData}
                    layout="vertical"
                    margin={{ top: 0, right: 24, left: 0, bottom: 0 }}
                    barSize={18}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--db-border)" />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 11, fill: 'var(--db-muted)' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={105}
                      tick={{ fontSize: 12, fill: 'var(--db-muted)' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,.03)' }} />
                    <Bar dataKey="value" name="Leads" radius={[0, 6, 6, 0]}>
                      {barData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Donut pie chart — status distribution */}
            <div className="db__card">
              <div className="db__card-header">
                <div>
                  <h2 className="db__card-title">Status Distribution</h2>
                  <p className="db__card-subtitle">Share of each status</p>
                </div>
              </div>
              {pieData.length === 0 ? (
                <p className="db__empty">No data yet.</p>
              ) : (
                <div className="donut-container">
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={68}
                        outerRadius={95}
                        paddingAngle={3}
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                        strokeWidth={0}
                      >
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ fontSize: '0.75rem', paddingTop: 8 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Center label */}
                  <div className="donut-center-label" style={{ top: '42%' }}>
                    <span className="donut-center-label__value">
                      <CountUp end={totalLeads} duration={1.4} separator="," />
                    </span>
                    <span className="donut-center-label__sub">Total</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Team Distribution (admin only) ─────────────────────────────── */}
          {isAdmin && teamData.length > 0 && (
            <div className="db__card">
              <div className="db__card-header">
                <div>
                  <h2 className="db__card-title">Leads by Team</h2>
                  <p className="db__card-subtitle">Distribution across all teams</p>
                </div>
                <Link to="/team" className="db__card-link">Manage teams →</Link>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={teamData}
                  margin={{ top: 4, right: 24, left: 0, bottom: 24 }}
                  barSize={36}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--db-border)" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: 'var(--db-muted)' }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    dy={6}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'var(--db-muted)' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,.03)' }} />
                  <Bar dataKey="count" name="Leads" radius={[6, 6, 0, 0]}>
                    {teamData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ── Recent Leads + Right Panel ──────────────────────────────────── */}
          <div className="db__bottom-grid">

            {/* Recent leads table */}
            <div className="db__card">
              <div className="db__card-header">
                <div>
                  <h2 className="db__card-title">Recent Leads</h2>
                  <p className="db__card-subtitle">Latest 10 entries</p>
                </div>
                <Link to="/leads" className="db__card-link">See all →</Link>
              </div>
              {recentLeads.length === 0 ? (
                <p className="db__empty">No leads to display.</p>
              ) : (
                <div className="db__table-wrap">
                  <table className="db__table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Business</th>
                        <th>State</th>
                        <th>Status</th>
                        {isAdmin && <th>Team</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {recentLeads.map((lead, i) => {
                        const meta = STATUS_META[lead.status] || { label: lead.status || 'Unknown', color: '#9ca3af' };
                        return (
                          <tr key={lead.id || i}>
                            <td>
                              <div className="lead-name">
                                <div className="lead-avatar" style={{ background: avatarColor(lead.name) }}>
                                  {(lead.name || '?')[0].toUpperCase()}
                                </div>
                                <div>
                                  <p className="lead-name__primary">{lead.name || '—'}</p>
                                  <p className="lead-name__secondary">{lead.email || lead.mobile || '—'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="td-muted">{lead.bname || '—'}</td>
                            <td className="td-muted">{lead.state || '—'}</td>
                            <td>
                              <span
                                className="status-pill"
                                style={{ background: meta.color + '20', color: meta.color }}
                              >
                                {meta.label}
                              </span>
                            </td>
                            {isAdmin && <td className="td-muted">{lead.ass || '—'}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Right panel: quick access + gauges */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Quick access */}
              <div className="db__card">
                <div className="db__card-header">
                  <h2 className="db__card-title">Quick Access</h2>
                </div>
                <div className="quick-actions-grid">
                  <QuickAction icon={Icon.leads} label="Leads"     to="/leads"     color="#7c3aed" />
                  <QuickAction icon={Icon.doc}   label="Documents" to="/documents" color="#0284c7" />
                  <QuickAction icon={Icon.note}  label="Notes"     to="/notes"     color="#d97706" />
                  <QuickAction icon={Icon.help}  label="Support"   to="/support"   color="#dc2626" />
                  {(isAdmin || isSubAdmin) && (
                    <QuickAction icon={Icon.team}  label="Team"    to="/team"      color="#059669" />
                  )}
                  {isAdmin && (
                    <QuickAction icon={Icon.admin} label="Admin"   to="/admin"     color="#7c3aed" />
                  )}
                </div>
              </div>

              {/* Conversion radial chart */}
              <div className="db__card">
                <div className="db__card-header">
                  <h2 className="db__card-title">Performance</h2>
                  <p className="db__card-subtitle" style={{ fontSize: '0.75rem', color: 'var(--db-muted)' }}>
                    Conversion &amp; activity rates
                  </p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <ResponsiveContainer width="100%" height={160}>
                    <RadialBarChart
                      cx="50%" cy="80%"
                      innerRadius="50%"
                      outerRadius="100%"
                      startAngle={180}
                      endAngle={0}
                      data={radialData}
                      barSize={14}
                    >
                      <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                      <RadialBar
                        dataKey="value"
                        cornerRadius={8}
                        background={{ fill: 'var(--db-bg)' }}
                      />
                      <Tooltip content={<ChartTooltip />} />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>

                <div style={{ textAlign: 'center', marginTop: -40 }}>
                  <span style={{ fontSize: '2rem', fontWeight: 750, color: '#059669', letterSpacing: -1 }}>
                    <CountUp end={convRate} duration={1.4} suffix="%" />
                  </span>
                  <p style={{ margin: '2px 0 0', fontSize: '0.73rem', color: 'var(--db-muted)' }}>
                    Conversion Rate
                  </p>
                </div>

                <div className="gauge-section" style={{ marginTop: 20 }}>
                  {[
                    { label: 'Conversion Rate', pct: convRate, color: '#059669', detail: `${soldCount} sold of ${totalLeads}` },
                    { label: 'Answering Rate',  pct: ansRate,  color: '#2563eb', detail: `${breakdown['answering'] || 0} answering` },
                    { label: 'Hot Lead Rate',   pct: totalLeads > 0 ? Math.round(hotCount / totalLeads * 100) : 0, color: '#d97706', detail: `${hotCount} hot leads` },
                  ].map((g, i) => (
                    <div key={i} className="gauge-row">
                      <div className="gauge-row__header">
                        <span className="gauge-row__label">{g.label}</span>
                        <span className="gauge-row__pct" style={{ color: g.color }}>{g.pct}%</span>
                      </div>
                      <div className="gauge-track">
                        <div
                          className="gauge-fill"
                          style={{ width: `${Math.max(g.pct, g.pct > 0 ? 2 : 0)}%`, background: g.color }}
                        />
                      </div>
                      <span className="gauge-row__foot">{g.detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
